import { Component, inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import {
    GetCompanyController,
    GetReportController,
    GetScenariosController,
    GetSeriesRepositoryController,
    PopulateReportController
} from "@arch/presentation/controllers";
import { CompanyEntity, ReportEntity, SerieEntity } from "@arch/domain/entity";
import { ReportHeaderComponent } from "@app/views/auth/report/components/report-header/report-header.component";
import { ReportBeachmarkingSectionComponent } from "@app/views/auth/report/components/report-beachmarking-section/report-beachmarking-section.component";
import { ReportToolbarComponent } from "@app/views/auth/report/components/report-toolbar/report-toolbar.component";
import { ReportPdfFooterComponent } from "@app/views/auth/report/components/report-pdf-footer/report-pdf-footer.component";
import { ReportPdfIndexComponent } from "@app/views/auth/report/components/report-pdf-index/report-pdf-index.component";
import { ReportPerformanceComponent } from "@app/views/auth/report/components/report-performance/report-performance.component";
import { PdfService } from "@app/shared/services/pdf.service";
import { ReportPreferenceService } from "@arch/domain/application/services";
import { TableBudgetComponent } from "@app/shared/components/table-budget/table-budget.component";
import { DateUtils } from "@arch/_shared/utils";

@Component({
    selector: "app-pdf-budget",
    imports: [
        SharedMaterialModule,
        SharedDsnModule,
        ReportHeaderComponent,
        ReportBeachmarkingSectionComponent,
        ReportToolbarComponent,
        ReportPdfFooterComponent,
        ReportPdfIndexComponent,
        ReportPerformanceComponent,
        TableBudgetComponent
    ],
    templateUrl: "./pdf.component.html",
    styles: [
        `
            .divider {
                border-bottom: var(--border-width-1) solid
                    var(--color-border-light);
            }
            .note-container {
                padding: var(--spacing-8);
                background-color: var(--color-expand-collapse-notes-bg);
                border-radius: var(--border-radius-8);
                gap: var(--spacing-8);
                .fa-sticky-note {
                    font-size: var(--font-size-12);
                }
            }
            .pdf-section-about-visualization {
                margin-top: var(--spacing-8);
            }
        `
    ],
    styleUrls: ["../../pdf-config.scss"]
})
export class PdfBudgetComponent implements OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly getReportController = inject(GetReportController);
    private readonly getCompanyController = inject(GetCompanyController);
    private readonly getScenariosController = inject(GetScenariosController);
    private readonly getSeriesRepositoryController = inject(
        GetSeriesRepositoryController
    );
    private readonly populateReportController = inject(
        PopulateReportController
    );
    public pdfService = inject(PdfService);
    public reportPreferenceService = inject(ReportPreferenceService);
    public report: ReportEntity;
    public company: CompanyEntity;
    public loading: boolean = true;
    public hasBenchmarking: boolean = true;
    public tableBudgetPages: {
        dates?: Date[];
        indexInterval: number[];
    }[] = [];

    async ngOnInit(): Promise<void> {
        this.loading = true;

        const { companyId, reportId } = this.route.snapshot.queryParams;
        if (!companyId || !reportId) {
            this.loading = false;
            this.router.navigate(["/"]);
            return;
        }

        const report = await this.getReportController.execute({
            reportId: reportId
        });

        this.reportPreferenceService.setAll(report.props?.optionsContent);

        const [company, scenarios] = await Promise.all([
            this.getCompanyController.execute({
                companyId: companyId,
                options: {
                    isGroup: report.props.isGroup
                }
            }),
            this.getScenariosController.execute({
                companyId,
                options: {
                    addOriginal: true,
                    addProjection: true
                }
            })
        ]);

        report.setScenarioOptions(scenarios);
        this.report = report;
        this.company = company;
        this.pdfService.config(this.report);

        const { notes, posts } = await this.populateReportController.execute({
            company: this.company,
            report: this.report,
            options: {
                performance: true,
                drilldown: true,
                totalMonthsProjection: 12,
                benchmarking: this.company.isGroup ? false : true,
                accountsPlan: { origin: "accountsPlan" }
            }
        });

        this.report.setNotes(notes);
        this.report.setPosts(posts);
        this.setTableBudgetPages();

        this.loading = false;

        this.pdfService.print();
    }

    private setTableBudgetPages(): void {
        const budgetSeriesData = this.getBudgetData();
        const budgetIndexes = budgetSeriesData.map((data, index) => index);
        budgetIndexes.push(budgetSeriesData.length);
        const seriesDataChunks = this.chunkSeriesData(
            budgetIndexes,
            !this.reportPreferenceService?.viewBench ? 35 : 34
        );

        for (const dates of this.pdfService.tableValuesDates) {
            for (const seriesData of seriesDataChunks) {
                this.tableBudgetPages.push({
                    dates,
                    indexInterval: seriesData
                });
            }
        }
    }

    // private getDates() {
    //     const allDates = DateUtils.getDatesInterval(
    //         this.report.options?.startDate,
    //         this.report.options?.endDate,
    //         this.report.options?.period
    //     );

    //     return allDates.slice(0, 3);
    // }

    private getBudgetData() {
        return this.getSeriesRepositoryController.execute(
            [],
            (serie: SerieEntity) =>
                serie.props.metadata.origin === "accountsPlans"
        );
    }

    public onBenchmarkingDataLoaded(hasBenchmarkingData: boolean): void {
        this.hasBenchmarking = hasBenchmarkingData;
    }

    private chunkSeriesData(
        seriesData: number[],
        chunkSize: number
    ): number[][] {
        if (!seriesData || !Array.isArray(seriesData)) return [];
        const seriesDataChunks: number[][] = [];
        let index = 0;
        if (!this.reportPreferenceService?.viewBench) {
            chunkSize = chunkSize - 8;
        }
        for (let i = 0; i < seriesData.length; i += chunkSize) {
            index++;

            if (this.reportPreferenceService?.viewBench) {
                seriesDataChunks.push(seriesData.slice(i, i + chunkSize));
            } else {
                if (index === 1) {
                    seriesDataChunks.push(seriesData.slice(i, chunkSize));
                } else {
                    seriesDataChunks.push(
                        seriesData.slice(i, i + chunkSize + 7)
                    );
                }
            }
        }
        return seriesDataChunks;
    }
}
